import {
	reactive,
	computed,
	toRefs,
	markRaw,
} from 'vue';

// stores
export const registeredStores = reactive([]);

// plugins
export const registeredPlugins = reactive([]);

// default options
export const defaultOptions = {
	state: () => {},
	getters: {},
	actions: {},
	plugins: [],
};

// create root store
export function createStore () {
	// debug(`[Store] Creating Store definition`);

	const rootStore = markRaw({
		install (app, options = {}) {
			app.config.globalProperties.$store = {
				options,
				defineStore,
				registeredStores,
				registeredPlugins,
			};

			// debug(`[Store] Root store created`);
		},
		use (plugin) {
			registeredPlugins.push(plugin);

			for (const store of registeredStores) {
				store.use(plugin);
			}
		},
	});

	return rootStore;
}

// define store
export function defineStore (id, options = {}) {
	// debug(`[Store ${id}] Creating store definition`);

	options = { ...defaultOptions, ...options, name: id };

	const registeredStore = registeredStores.find(store => store.id === id);
	if (registeredStore) {
		// debug(`[Store ${id}] Store already defined`);
		return registeredStore.define;
	}

	// state
	const source = options.state();
	const state = reactive(source);

	// getters
	const getters = {};
	const gettersContext = reactive({ ...toRefs(state) });
	for (const key in options.getters) {
		getters[key] = gettersContext[key] = computed(() => options.getters[key].bind(gettersContext)(state));
	}

	// actions
	const actions = {};
	const actionsContext = reactive({ ...toRefs(state), ...getters });
	for (const key in options.actions) {
		actions[key] = actionsContext[key] = options.actions[key].bind(actionsContext);
	}

	// plugins (async execution to support async plugins like persist)
	const storePlugins = options.plugins ?? [];
	const pluginContext = reactive({ options, state, actions, store: { ...toRefs(state), ...getters, ...actions } });

	// Execute plugins and track promises for async plugins
	const pluginPromises = [];
	for (const plugin of [...registeredPlugins, ...storePlugins]) {
		const result = plugin(pluginContext);
		if (result instanceof Promise) {
			pluginPromises.push(result);
		}
		storePlugins.push(plugin);
	}

	// Wait for all async plugins to complete before marking store as ready
	if (pluginPromises.length > 0) {
		Promise.all(pluginPromises).then(() => {
			if (!options.isReady) {
				// If no custom isReady, set a default ready flag
				state.__pluginsReady = true;
			}
		});
	} else {
		state.__pluginsReady = true;
	}

	// is ready function
	function isReady () {
		if (options.isReady) {
			return options.isReady();
		}

		return new Promise((resolve) => {
			resolve(true);
		});
	}

	// store
	const store = () => {
		return {
			isReady,
			...toRefs(state),
			...getters,
			...actions,
		};
	};

	// register store
	registeredStores.push({
		id,
		define: store,
		use: (plugin) => {
			const result = plugin(pluginContext);
			// Handle async plugins in use() method as well
			if (result instanceof Promise) {
				result.catch(err => {
					console.error(`[Store ${id}] Plugin error:`, err);
				});
			}
		},
	});

	return store;
}

// store debugger
function debug (...message) {
	console.log(...message);
}
